import { getStoreState } from 'config/store'
import { getActiveConversationId } from 'modules/conversations/selectors'
import { getEndUserEmail, getEndUserId } from 'modules/session/selectors'
import { pathOr } from 'ramda'
import { PUBLIC_EVENTS } from 'shared/constants/api'
import { ConversationEventType, ConversationId } from 'types/conversation'
import { Message } from 'types/message'
import { getTextFromHTML } from 'utils'
import { createEvent } from 'utils/events'
import { parseHtmlText } from 'utils/html'

interface MeetingBookedAPIEventArgs {
  agentId: number,
  meetingTime: number,
  duration: number,
  timeZone: string,
  conversationId: ConversationId
}

const hydrateWithActiveCampaignData = (payload: Record<string, any> = {}) => {
  const { campaigns = {}, conversations = {} } = getStoreState()

  if (payload && payload.conversationId) {
    const conversationId = pathOr(null, ['activeConversation'], conversations)
    if (payload.conversationId !== conversationId) {
      return payload
    }
  }

  const campaignId = pathOr(null, ['activeCampaign', 'attributes', 'campaignId'], campaigns)
  const interactionId = pathOr(null, ['activeCampaign', 'attributes', 'startInteraction'], campaigns)
  const playbookId = pathOr(null, ['activeCampaign', 'attributes', 'playbookId'], campaigns)

  return {
    ...payload,
    campaignId,
    interactionId,
    playbookId
  }
}

const createMessageEventPayload = (message: Partial<Message>) => {
  return hydrateWithActiveCampaignData({
    inboxId: message.inboxId,
    conversationId: message.conversationId
  })
}

const getTeamUser = (id: number) => {
  if (!id) {
    return null
  }

  const team = pathOr([], ['embed', 'configuration', 'team'], getStoreState())

  return team.find(user => user.id === id)
}

export const triggerButtonClickedAPIEvent = (message: Partial<Message> = {}) => {
  const questionId = pathOr(null, ['attributes', 'questionId'], message)
  const payload = hydrateWithActiveCampaignData({
    createdAt: message.createdAt,
    authorId: message.authorId,
    conversationId: message.conversationId,
    buttonBody: parseHtmlText(message.body),
    questionId,
  })

  createEvent(PUBLIC_EVENTS.CONVERSATION_BUTTON_CLICKED, payload)
}

export const triggerConversationFirstInteractionAPIEvent = (message: Message) => {
  let hydratePayload: Record<string,any> = {
    createdAt: message.createdAt,
    authorId: message.authorId,
  }

  if (message.conversationId) {
    hydratePayload.conversationId = message.conversationId
  }
  if (message.id) {
    hydratePayload.messageId = message.id
  }

  const payload = hydrateWithActiveCampaignData(hydratePayload)

  createEvent(PUBLIC_EVENTS.CONVERSATION_INTERACTED, payload)
}

export const triggerEmailCapturedAPIEvent = ({ email }: { email?: string }) => {
  const state = getStoreState()
  const activeConversationId = getActiveConversationId(state) || null

  const payload = {
    data: hydrateWithActiveCampaignData({
      email,
      conversationId: activeConversationId
    })
  }

  createEvent(PUBLIC_EVENTS.EMAIL_CAPTURED, payload)
}

export const triggerMeetingRequestedAPIEvent = (agentId: number) => {
  const teamMembers = pathOr([], ['embed', 'configuration', 'team'], getStoreState())
  const bookingAgents = teamMembers.filter(teamMember => teamMember.id === agentId)

  if(!!bookingAgents.length) {
    const bookingAgent = bookingAgents[0]
    const payload = {
      teamMember: {
        id: bookingAgent.id,
        name: bookingAgent.name
      }
    }

    createEvent(PUBLIC_EVENTS.MEETING_REQUESTED, payload)
  }
}

export const triggerMeetingScheduledAPIEvent = (eventPayload: MeetingBookedAPIEventArgs) => {
  const { agentId, meetingTime, duration, timeZone, conversationId } = eventPayload
  const state = getStoreState()
  const activeConversationMessages = pathOr({}, ['conversations', 'messages', conversationId], state)
  const firstMessage = activeConversationMessages[Object.keys(activeConversationMessages)[0]]

  const teamMembers = pathOr([], ['embed', 'configuration', 'team'], state)
  const bookingAgent = teamMembers.filter(teamMember => teamMember.id === agentId)[0]

  if(firstMessage) {
    const campaignId = pathOr(null, ['attributes', 'campaignId'], firstMessage)
    const playbookId = pathOr(null, ['attributes', 'playbookId'], firstMessage)
    const interactionId = pathOr(null, ['attributes', 'startInteraction'], firstMessage)

    const payload = {
      teamMember: {
        id: bookingAgent.id,
        name: bookingAgent.name
      },
      meeting: {
        time: meetingTime,
        duration,
        timeZone
      },
      conversationId,
      playbookId,
      campaignId,
      interactionId
    }

    createEvent(PUBLIC_EVENTS.MEETING_BOOKED, payload)
  }
}

export const triggerMessageSentAPIEvent = (message: Partial<Message> = {}): void => {
  createEvent(PUBLIC_EVENTS.MESSAGE_SENT, createMessageEventPayload(message))
}

export const triggerMessageReceivedAPIEvent = (message: Partial<Message> = {}): void => {
  const user = getTeamUser(message.authorId) || {}
  const payload = {
    ...createMessageEventPayload(message),
    teamMember: {
      id: user.id,
      name: user.name
    }
  }
  createEvent(PUBLIC_EVENTS.MESSAGE_RECEIVED, payload)
}

export const triggerPhoneCapturedAPIEvent = (message: Message, phone: string) => {
  const payload = hydrateWithActiveCampaignData({
    createdAt: message.createdAt,
    authorId: message.authorId,
    conversationId: message.conversationId,
    phone
  })
  createEvent(PUBLIC_EVENTS.PHONE_NUMBER_CAPTURED, payload)
}

export const triggerPlaybookClickedAPIEvent = (message?: Partial<Message>) => {
  const {
    conversationId,
  } = message || {}
  const payload = hydrateWithActiveCampaignData({
    conversationId
  })
  createEvent(PUBLIC_EVENTS.CONVERSATION_PLAYBOOK_CLICKED, payload)
}

export const triggerConversationSelectedAPIEvent = (conversationId: ConversationId) => {
  const payload = hydrateWithActiveCampaignData({
    conversationId
  })
  createEvent(PUBLIC_EVENTS.CONVERSATION_SELECTED, payload)
}

export const triggerPlaybookDismissedAPIEvent = (message?: Partial<Message>) => {
  const {
    conversationId,
    id: messageId
  } = message || {}
  const payload = hydrateWithActiveCampaignData({
    conversationId,
    messageId
  })
  createEvent(PUBLIC_EVENTS.CONVERSATION_PLAYBOOK_DISMISSED, payload)
}

export const triggerSliderClosedAPIEvent = async (sliderMessage: string) => {
  const payload = {
    botMessage: getTextFromHTML(sliderMessage),
    ...hydrateWithActiveCampaignData()
  }

  createEvent(PUBLIC_EVENTS.SLIDER_CLOSED, payload)
}

export const triggerCampaignActionAPIEvent = async (eventType: ConversationEventType, campaignId: number) => {
  if (!eventType || !campaignId) {
    return
  }
  const state = getStoreState()
  const isWidgetVisible = pathOr(false, ['view', 'matchedTargeting'], state)
  const isWidgetOnline = pathOr(false, ['targeting', 'isOnline'], state)

  const payload = {
    campaignId,
    data: {
      isOnline: isWidgetOnline,
      sidebarOpen: false,
      widgetVisible: isWidgetVisible
    }
  }

  switch (eventType) {
    case ConversationEventType.CLICKED:
      createEvent(PUBLIC_EVENTS.CAMPAIGN_CLICKED, payload)
      break
    case ConversationEventType.SUBMITTED:
      createEvent(PUBLIC_EVENTS.CAMPAIGN_SUBMITTED, payload)
      break
    case ConversationEventType.OPENED:
      createEvent(PUBLIC_EVENTS.CAMPAIGN_OPENED, payload)
      break
    case ConversationEventType.DISMISSED:
      createEvent(PUBLIC_EVENTS.CAMPAIGN_DISMISSED, payload)
      break
    default:
      break
  }
}

export const triggerPlaybookFiredAPIEvent = (message: Partial<Message>) => {
  const {
    authorId,
    conversationId,
    createdAt,
    id: messageId,
    attributes: {
      playbookId,
      startInteraction,
      interactionId,
      campaignId
    } = { playbookId: null, startInteraction: null, interactionId: null, campaignId: null }
  } = message || {}
  createEvent(PUBLIC_EVENTS.CONVERSATION_PLAYBOOK_FIRED, {
    messageId,
    createdAt,
    authorId,
    conversationId,
    interactionId: interactionId || startInteraction,
    playbookId,
    campaignId
  })
}

export const triggerConversationStartedAPIEvent = (message: Partial<Message> = {}): void => {
  const state = getStoreState()
  const endUserId = getEndUserId(state)
  const endUserEmail = getEndUserEmail(state)

  const payload = {
    inboxId: message.inboxId,
    conversationId: message.conversationId,
    interactionId: message.attributes.interactionId,
    endUserId,
    endUserEmail
  }
  createEvent(PUBLIC_EVENTS.CONVERSATION_STARTED, payload)
}

export const triggerWelcomeMessageAPIEvent = (open: boolean): void => {
  const openEvent = open
    ? PUBLIC_EVENTS.WELCOME_MESSAGE_OPENED
    : PUBLIC_EVENTS.WELCOME_MESSAGE_CLOSED

  createEvent(openEvent)
}

export const triggerAwayMessageAPIEvent = (open: boolean): void => {
  const openEvent = open
    ? PUBLIC_EVENTS.AWAY_MESSAGE_OPENED
    : PUBLIC_EVENTS.AWAY_MESSAGE_CLOSED

  createEvent(openEvent)
}


export const createTriggerChatVisibleAPIEvent = () => {
  let previousOpenState = false

  return (open: boolean): void => {
    open = !!open
    if (previousOpenState === open) {
      return
    }

    const chatEvents = open
      ? [PUBLIC_EVENTS.CHAT_OPENED, PUBLIC_EVENTS.SIDEBAR_OPENED]
      : [PUBLIC_EVENTS.CHAT_CLOSED, PUBLIC_EVENTS.SIDEBAR_CLOSED]

    chatEvents.forEach((chatEvent) => createEvent(chatEvent))

    previousOpenState = open
  }
}

export const triggerChatVisibleAPIEvent = createTriggerChatVisibleAPIEvent()


export const triggerUserAttributesUpdatedEvent = () => {
  createEvent(PUBLIC_EVENTS.USER_ATTRIBUTES_UPDATED)
}
